/**
 * Collection Grid Web Component
 *
 * Loads all products from a collection, sorts them by priority tags,
 * and displays them with infinite scroll.
 */
class CollectionGrid extends HTMLElement {
  constructor() {
    super();
    this.allProducts = [];       // Sorted array of product HTML elements
    this.displayedCount = 0;
    this.isLoading = false;
    this.scrollObserver = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-collection-grid]');
    this.loadingState = this.querySelector('[data-loading-state]');
    this.gridContainer = this.querySelector('[data-grid-container]');
    this.sentinel = this.querySelector('[data-scroll-sentinel]');
    this.loadingMore = this.querySelector('[data-loading-more]');
    this.emptyState = this.querySelector('[data-empty-state]');

    this.collectionHandle = this.dataset.collectionHandle;
    this.sectionId = this.dataset.sectionId;
    this.productsPerLoad = parseInt(this.dataset.productsPerLoad, 10) || 24;
    this.priorityTags = this.dataset.priorityTags
      ? this.dataset.priorityTags.split(',').map(t => t.trim().toLowerCase())
      : [];
    this.totalProducts = parseInt(this.dataset.totalProducts, 10) || 0;

    // Don't initialize if no collection selected
    if (!this.collectionHandle) {
      this.loadingState.style.display = 'none';
      this.emptyState.style.display = '';
      return;
    }

    // Load products on first scroll or after browser idle
    this.initLazyLoad();
  }

  initLazyLoad() {
    // Fire the load exactly once, from whichever trigger comes first.
    const onFirstScroll = () => this.trigger(onFirstScroll);

    window.addEventListener('scroll', onFirstScroll, { passive: true });

    // Also load after a short idle period if the user doesn't scroll, to handle
    // cases where the grid is visible without scrolling.
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => this.trigger(onFirstScroll), { timeout: 2000 });
    } else {
      setTimeout(() => this.trigger(onFirstScroll), 1000);
    }
  }

  trigger(onFirstScroll) {
    if (this.initialized) return;
    this.initialized = true;
    window.removeEventListener('scroll', onFirstScroll);
    // The first page now renders from inline server-rendered markup with no
    // fetch, so there is nothing to race against page load. loadAllProducts()
    // itself waits for the load event before any network request (large
    // collections only) — see pageReady().
    this.loadAllProducts();
  }

  disconnectedCallback() {
    if (this.scrollObserver) this.scrollObserver.disconnect();
  }

  getCacheKey() {
    // The cached markup is rendered product cards, so it carries prices,
    // availability and any market-specific catalog differences. Namespace by
    // country as well as currency: two markets can share a currency and still
    // differ in catalog, fixed pricing and availability.
    const loc = (window.theme && window.theme.localization) || {};
    const market = `${loc.countryCode || 'XX'}-${loc.currencyCode || 'XXX'}`;
    return `collection-grid-${market}-${window.location.pathname}-${this.collectionHandle}`;
  }

  getCachedProducts() {
    // sessionStorage access can throw in iOS Safari Private Browsing / when
    // storage is disabled, so the whole read is guarded.
    try {
      const cacheKey = this.getCacheKey();
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;

      const { html, timestamp } = JSON.parse(cached);
      const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
      if (Date.now() - timestamp > CACHE_EXPIRY) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }
      return html; // Array of outerHTML strings
    } catch (e) {
      return null;
    }
  }

  cacheProducts(products) {
    const cacheKey = this.getCacheKey();
    const html = products.map(el => el.outerHTML);
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        html,
        timestamp: Date.now()
      }));
    } catch (e) {
      // sessionStorage may be full or unavailable
      console.log('Collection Grid: Could not cache products', e);
    }
  }

  loadFromCache(htmlArray) {
    const parser = new DOMParser();
    this.allProducts = htmlArray.map(html => {
      const doc = parser.parseFromString(html, 'text/html');
      return doc.body.firstChild;
    });

    // Hide loading, show grid
    this.loadingState.style.display = 'none';
    this.gridContainer.style.display = '';

    // Display first batch
    this.displayNextBatch();

    // Setup infinite scroll if needed
    if (this.displayedCount < this.allProducts.length) {
      this.sentinel.style.display = '';
      this.initInfiniteScroll();
    }
  }

  async loadAllProducts() {
    // Instant back-button navigation from the in-session cache.
    const cachedHTML = this.getCachedProducts();
    if (cachedHTML) {
      this.loadFromCache(cachedHTML);
      return;
    }

    const allProductElements = [];
    let page = 1;
    let hasMore = true;
    let loadFailed = false;

    // Seed the first page from the inline server-rendered <template
    // data-product-source>. The products are already in the DOM, so a collection
    // that fits on one page needs NO network request and therefore cannot hit the
    // intermittent iOS Safari "TypeError: Load failed" that this section suffered.
    const inlineSource = this.querySelector('[data-product-source]');
    if (inlineSource) {
      this.collectProducts(this.getProductBlocksFrom(inlineSource), allProductElements);
      hasMore = inlineSource.dataset.hasNextPage === 'true';
      page = 2; // page 1 came from the DOM; only fetch the remainder (if any)
      inlineSource.remove(); // free the inert template — we cloned what we need
    }

    // Common case: the whole collection fit on the inline first page. Render
    // immediately — no network, no waiting on the load event. 100% reliable.
    if (!hasMore && allProductElements.length > 0) {
      this.renderProducts(allProductElements);
      return;
    }

    // More pages are needed (or the inline source was somehow missing). Defer
    // network work until the page has finished loading — a fetch that races page
    // load is the classic trigger for Safari's "Load failed". Any failure from
    // here on is non-fatal: we keep and show whatever already loaded.
    await this.pageReady();

    while (hasMore) {
      const url = `/collections/${this.collectionHandle}?page=${page}&section_id=${this.sectionId}`;

      try {
        const html = await this.fetchPage(url);
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const sourceContainer = doc.querySelector('[data-product-source]');
        if (!sourceContainer) {
          hasMore = false;
          break;
        }

        const blocks = this.getProductBlocksFrom(sourceContainer);
        const hasNextPage = sourceContainer.dataset.hasNextPage === 'true';

        if (blocks.length === 0) {
          hasMore = false;
        } else {
          this.collectProducts(blocks, allProductElements);
          if (hasNextPage) {
            page++;
          } else {
            hasMore = false;
          }
        }

        // Safety limit to prevent infinite loops
        if (page > 100) hasMore = false;

      } catch (error) {
        // Keep whatever already loaded rather than discarding everything; a
        // partial grid beats an all-or-nothing error on a flaky mobile network.
        console.error('Collection Grid: error loading page', page, error);
        loadFailed = true;
        hasMore = false;
      }
    }

    // Nothing loaded at all. Distinguish a real fetch failure (retry UI) from a
    // genuinely empty collection (empty state). With inline seeding this branch
    // is only reached when there was no server-rendered source AND the network
    // failed — a transient iOS fetch flake can no longer masquerade as "empty".
    if (allProductElements.length === 0) {
      if (loadFailed) {
        this.showError();
      } else {
        this.loadingState.style.display = 'none';
        this.emptyState.style.display = '';
      }
      return;
    }

    this.renderProducts(allProductElements);
  }

  // Sort (priority products first), cache, and paint the grid + infinite scroll.
  renderProducts(productData) {
    this.allProducts = this.sortProducts(productData);
    this.cacheProducts(this.allProducts);

    this.loadingState.style.display = 'none';
    this.gridContainer.style.display = '';

    this.displayNextBatch();

    if (this.displayedCount < this.allProducts.length) {
      this.sentinel.style.display = '';
      this.initInfiniteScroll();
    }
  }

  // Resolves once the page has fully loaded, so background fetches never race the
  // initial page load (a common cause of Safari "Load failed").
  pageReady() {
    if (document.readyState === 'complete') return Promise.resolve();
    return new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  }

  // Returns the product-block elements inside a [data-product-source] container,
  // transparently handling a <template> (inert .content) or a plain element.
  getProductBlocksFrom(sourceEl) {
    if (!sourceEl) return [];
    const root = sourceEl.content || sourceEl; // <template> exposes its .content
    return Array.from(root.querySelectorAll('[data-product-block]'));
  }

  // Clone each block and record its tags/id for sorting. Clones stay inert until
  // appended to the live grid, where they upgrade as <product-block> custom
  // elements — identical handling whether the source was inline or fetched.
  collectProducts(blocks, target) {
    blocks.forEach((product) => {
      target.push({
        element: product.cloneNode(true),
        tags: this.extractTags(product),
        id: product.dataset.productId
      });
    });
  }

  async fetchPage(url, attempts = 3) {
    // Background fetch for additional pages. iOS Safari can throw
    // "TypeError: Load failed" on a flaky request and can also leave a request
    // hanging indefinitely, so each attempt is bounded by an AbortController
    // timeout and auto-retried with backoff.
    //
    // Default caching is intentional: `no-store` forces a fresh network round
    // trip every time, which *increases* exposure to exactly the transient
    // failures we are guarding against. A few-minutes-stale section render is
    // already acceptable (the sessionStorage cache below implies as much), and
    // the page-1 render — the part that must be current — now comes from the
    // server-rendered inline markup, not from here.
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.text();
      } catch (error) {
        clearTimeout(timeout);
        lastError = error;
        if (attempt < attempts) {
          await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        }
      }
    }
    throw lastError;
  }

  showError() {
    this.loadingState.style.display = 'none';
    this.emptyState.style.display = '';
    this.emptyState.innerHTML =
      '<p>Sorry, products couldn’t be loaded.</p>' +
      '<button type="button" class="btn btn--secondary" data-retry-load>Try again</button>';
    const retry = this.emptyState.querySelector('[data-retry-load]');
    if (retry) {
      retry.addEventListener('click', () => {
        this.emptyState.style.display = 'none';
        this.loadingState.style.display = '';
        this.loadAllProducts();
      }, { once: true });
    }
  }

  extractTags(productElement) {
    // Tags stored in data attribute on product-block
    const tagsAttr = productElement.dataset.productTags || '';
    return tagsAttr
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t !== '');
  }

  sortProducts(products) {
    // If no priority tags configured, return original order
    if (this.priorityTags.length === 0) {
      return products.map(p => p.element);
    }

    const priority = [];
    const regular = [];

    products.forEach((product) => {
      const hasPriorityTag = product.tags.some(tag =>
        this.priorityTags.includes(tag)
      );

      if (hasPriorityTag) {
        priority.push(product.element);
      } else {
        regular.push(product.element);
      }
    });

    return [...priority, ...regular];
  }

  displayNextBatch() {
    const end = Math.min(
      this.displayedCount + this.productsPerLoad,
      this.allProducts.length
    );

    const fragment = document.createDocumentFragment();

    for (let i = this.displayedCount; i < end; i++) {
      fragment.appendChild(this.allProducts[i]);
    }

    this.grid.appendChild(fragment);
    this.displayedCount = end;

    // Trigger animations if the theme uses them
    if (typeof theme !== 'undefined' && typeof theme.initAnimateOnScroll === 'function') {
      theme.initAnimateOnScroll();
    }

    // Hide sentinel if no more products
    if (this.displayedCount >= this.allProducts.length) {
      this.sentinel.style.display = 'none';
      if (this.scrollObserver) this.scrollObserver.disconnect();
    }
  }

  initInfiniteScroll() {
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading) {
          this.isLoading = true;
          this.loadingMore.style.display = '';

          // Small delay for visual feedback
          setTimeout(() => {
            this.displayNextBatch();
            this.loadingMore.style.display = 'none';
            this.isLoading = false;
          }, 300);
        }
      },
      { rootMargin: '200px' }
    );

    this.scrollObserver.observe(this.sentinel);
  }
}

// Guard against re-definition: this section injects the script once per instance,
// so a page with multiple collection grids would otherwise throw on the 2nd define.
if (!customElements.get('collection-grid')) {
  customElements.define('collection-grid', CollectionGrid);
}
